/**
 * Layer panel of the read-only composite plan (patterns §45).
 *
 * The read-only sibling of the plan editor's LayerPanel: same vocabulary, same
 * eye toggles and opacity sliders, none of the editor state. It renders from
 * plain props, so the patterns page feeds it static data.
 */
import { useTranslation } from 'react-i18next'
import { mdiEyeOffOutline, mdiEyeOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { NetworkSwatch } from '../plan-editor/NetworkControls'
import {
    DEFAULT_LAYERS,
    redrawnOnly,
    underlayState,
    type CompositePlanLayers,
    type VectorLayer,
} from './composite-plan-layers'

export interface CompositePlanLayerRow {
    key: string
    label: string
}

/** One family of the networks drawn on the plan, as the layer panel lists it. */
export interface CompositePlanNetworkFamily {
    family: string
    label: string
    networks: { code: string, label: string, color?: string, dash?: string, count: number }[]
}

interface CompositePlanLayerPanelProps {
    underlays: CompositePlanLayerRow[]
    vectorLayers: VectorLayer[]
    layers: CompositePlanLayers
    onChange: (next: CompositePlanLayers) => void
    /** The networks the runs layer holds, by family; absent, the layer has a single switch. */
    networks?: CompositePlanNetworkFamily[]
    className?: string
}

function EyeToggle({ on, label, testId, onClick, size = 'sm' }: {
    on: boolean
    label: string
    testId: string
    onClick: () => void
    size?: 'xs' | 'sm'
}) {
    const { t } = useTranslation('assets')
    return (
        <button
            type="button"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={t(on ? 'planEditor.hide_layer' : 'planEditor.show_layer', { layer: label })}
            aria-pressed={on}
            title={label}
            data-testid={testId}
            onClick={onClick}
        >
            <AppIcon path={on ? mdiEyeOutline : mdiEyeOffOutline} size={size} />
        </button>
    )
}

export function CompositePlanLayerPanel({ underlays, vectorLayers, layers, onChange, networks, className }: CompositePlanLayerPanelProps) {
    const { t } = useTranslation('assets')
    const underlaysLabel = t('planEditor.layers.underlays')
    const isRedrawnOnly = !layers.underlaysOn && vectorLayers.every((l) => layers.vector[l]) && layers.hiddenNetworks.length === 0

    const hidden = new Set(layers.hiddenNetworks)
    // Hide or show a set of networks together: one network, or a whole family.
    const setNetworks = (codes: string[], hide: boolean) => {
        const next = new Set(hidden)
        for (const code of codes) {
            if (hide) next.add(code)
            else next.delete(code)
        }
        onChange({ ...layers, hiddenNetworks: [...next].sort() })
    }

    const setUnderlay = (key: string, patch: Partial<{ visible: boolean, opacity: number }>) => {
        onChange({
            ...layers,
            underlays: { ...layers.underlays, [key]: { ...underlayState(layers, key), ...patch } },
        })
    }

    return (
        <div className={'flex flex-col overflow-hidden ' + (className ?? '')} data-testid="composite-plan-layers">
            <div className="border-b border-border px-3 py-2">
                <h3 className="text-sm font-semibold text-foreground">{t('planEditor.layers_title')}</h3>
                {underlays.length > 0 && vectorLayers.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                        <Button
                            variant={isRedrawnOnly ? 'secondary' : 'outline'}
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            aria-pressed={isRedrawnOnly}
                            data-testid="composite-plan-layers-preset-redrawn"
                            onClick={() => onChange(redrawnOnly(layers))}
                        >
                            {t('compositePlan.preset_redrawn_only')}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            data-testid="composite-plan-layers-preset-all"
                            onClick={() => onChange(DEFAULT_LAYERS)}
                        >
                            {t('compositePlan.preset_show_all')}
                        </Button>
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-y-auto">
                {vectorLayers.length > 0 && (
                    <div className="border-b border-border/60 py-1" data-testid="composite-plan-layers-vector">
                        <div className="px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t('compositePlan.vector_title')}
                        </div>
                        {vectorLayers.map((layer) => {
                            const label = t(`planEditor.layers.${layer}`)
                            const on = layers.vector[layer]
                            return (
                                <div key={layer}>
                                    <div className="flex items-center gap-2 px-3 py-1">
                                        <EyeToggle
                                            on={on}
                                            label={label}
                                            testId={`composite-plan-layer-toggle-${layer}`}
                                            onClick={() => onChange({ ...layers, vector: { ...layers.vector, [layer]: !on } })}
                                        />
                                        <span className={'text-xs ' + (on ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
                                    </div>
                                    {layer === 'runs' && on && networks?.map((f) => {
                                        const codes = f.networks.map((n) => n.code)
                                        const familyOn = !codes.every((c) => hidden.has(c))
                                        return (
                                            <div key={f.family} data-testid={`composite-plan-network-family-${f.family}`}>
                                                <div className="flex items-center gap-2 py-1 pl-6 pr-3">
                                                    <EyeToggle
                                                        on={familyOn}
                                                        label={f.label}
                                                        size="xs"
                                                        testId={`composite-plan-network-family-toggle-${f.family}`}
                                                        onClick={() => setNetworks(codes, familyOn)}
                                                    />
                                                    <span className="flex-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{f.label}</span>
                                                </div>
                                                {f.networks.map((n) => {
                                                    const networkOn = !hidden.has(n.code)
                                                    return (
                                                        <div key={n.code} className="flex items-center gap-2 py-0.5 pl-10 pr-3 text-xs">
                                                            <EyeToggle
                                                                on={networkOn}
                                                                label={n.label}
                                                                size="xs"
                                                                testId={`composite-plan-network-toggle-${n.code}`}
                                                                onClick={() => setNetworks([n.code], networkOn)}
                                                            />
                                                            <NetworkSwatch color={n.color} dash={n.dash} />
                                                            <span className={'min-w-0 flex-1 truncate ' + (networkOn ? 'text-foreground' : 'text-muted-foreground line-through')} title={n.label}>{n.label}</span>
                                                            <span className="shrink-0 text-[10px] text-muted-foreground">{n.count}</span>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                )}

                {underlays.length > 0 && (
                    <div className="py-1" data-testid="composite-plan-layers-underlays">
                        <div className="flex items-center gap-2 px-3 py-1">
                            <EyeToggle
                                on={layers.underlaysOn}
                                label={underlaysLabel}
                                testId="composite-plan-layer-toggle-underlays"
                                onClick={() => onChange({ ...layers, underlaysOn: !layers.underlaysOn })}
                            />
                            <span className="flex-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                {underlaysLabel} ({underlays.length})
                            </span>
                        </div>
                        {underlays.map((u) => {
                            const state = underlayState(layers, u.key)
                            const dimmed = !layers.underlaysOn || !state.visible
                            return (
                                <div
                                    key={u.key}
                                    className="flex items-center gap-2 py-1 pl-6 pr-3 text-xs"
                                    data-testid={`composite-plan-underlay-row-${u.key}`}
                                >
                                    <EyeToggle
                                        on={state.visible}
                                        label={u.label}
                                        size="xs"
                                        testId={`composite-plan-underlay-toggle-${u.key}`}
                                        onClick={() => setUnderlay(u.key, { visible: !state.visible })}
                                    />
                                    <span
                                        className={'min-w-0 flex-1 truncate ' + (dimmed ? 'text-muted-foreground' : 'text-foreground')}
                                        title={u.label}
                                    >
                                        {u.label}
                                    </span>
                                    <input
                                        type="range"
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        value={state.opacity}
                                        disabled={dimmed}
                                        onChange={(e) => setUnderlay(u.key, { opacity: Number(e.target.value) })}
                                        aria-label={t('planEditor.layer_opacity', { layer: u.label })}
                                        className="h-1 w-14 shrink-0 accent-[var(--primary)] disabled:opacity-40"
                                        data-testid={`composite-plan-underlay-opacity-${u.key}`}
                                    />
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}
